import React from 'react'
import {Box, Chip, Paper, Stack, Typography} from "@mui/material"
import {Link as RouterLink} from "react-router-dom"
import {ProjectJson} from "../../../api/json/ProjectJson"
import ProjectActionsMenu from "./ProjectActionsMenu"
import {RoleCheck} from "../../../library/RoleCheck"
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined"
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined"
import ProjectTypeIcon from "../ProjectTypeIcon"
import {resolveNameForProjectType} from "../../../api/enum/ProjectType"

function timeAgo(dateString: string): string {
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000)
    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    const months = Math.floor(days / 30)
    return `${months}mo ago`
}

interface ProjectCardProps {
    project: ProjectJson
    didRename: (updated: ProjectJson) => void
    didCopy: (copy: ProjectJson) => void
    didDelete: (deletedId: string) => void
}

function ProjectCard({project, didRename, didCopy, didDelete}: ProjectCardProps) {
    return (
        <Paper
            component={RouterLink}
            to={`/projects/${project.id}`}
            sx={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                p: 3,
                borderRadius: '8px',
                border: '1px solid #ececf1',
                bgcolor: '#fff',
                cursor: 'pointer',
                transition: 'box-shadow 150ms ease, border-color 150ms ease',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    borderColor: '#d0d0d8',
                },
            }}
            elevation={0}
        >
            <Stack spacing={2} sx={{height: '100%'}}>
                {/* Name + actions on one line */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{fontWeight: 600, fontSize: '1rem', lineHeight: 1.3, minWidth: 0}} noWrap>
                        {project.name}
                    </Typography>
                    {RoleCheck.atLeastManager() &&
                        <Box
                            onClick={(e) => e.preventDefault()}
                            sx={{ml: 1, mr: -1, flexShrink: 0}}
                        >
                            <ProjectActionsMenu
                                project={project}
                                didCopy={didCopy}
                                didRename={didRename}
                                didDelete={didDelete}
                            />
                        </Box>
                    }
                </Stack>

                {/* Stats row */}
                <Stack direction="row" spacing={1} sx={{mt: 'auto'}}>
                    <Chip
                        icon={<ProjectTypeIcon type={project.type} sx={{fontSize: 14}}/>}
                        label={resolveNameForProjectType(project.type)}
                        size="small"
                        variant="outlined"
                        sx={{fontSize: '0.75rem', height: 26, px: 0.5}}
                    />
                    {project.stats.totalRows > 0 &&
                        <Chip
                            icon={<TableChartOutlinedIcon sx={{fontSize: 14}}/>}
                            label={`${project.stats.totalRows.toLocaleString()} rows`}
                            size="small"
                            variant="outlined"
                            sx={{fontSize: '0.75rem', height: 26, px: 0.5}}
                        />
                    }
                    {project.stats.numberOfCustomModels > 0 &&
                        <Chip
                            icon={<RocketLaunchOutlinedIcon sx={{fontSize: 14}}/>}
                            label={`${project.stats.numberOfCustomModels} model${project.stats.numberOfCustomModels !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{fontSize: '0.75rem', height: 26, px: 0.5}}
                        />
                    }
                </Stack>

                {/* Updated timestamp */}
                <Typography sx={{fontSize: '0.7rem', color: '#bbb'}}>
                    Updated {timeAgo(project.updated)}
                </Typography>
            </Stack>
        </Paper>
    )
}

export default ProjectCard
